import { Claim, ClaimEvaluationRequest, ClaimsServerResponse } from './types';
import { ClaimsAction, receivedUpdatedClaim, setClaims, setClaimsFetching } from './actions';
import { all, call, delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { ApiResponse } from 'apisauce';
import { HealAPI } from '../../healApi';
import { ReduxAction } from '../core';
import { RootState } from '../types';
import { UIAction } from '../ui/actions';
import { toast } from '../message/actions';

function* getClaimsData() {
  const state: RootState = yield select((s) => s);

  const claimStatesSelected = (state.ui.claimsStatus || []).length;

  if (!state.ui.claimsSearchText && claimStatesSelected === 0) {
    // don't allow a completely unfiltered search
    yield put(setClaims([]));
    yield put(setClaimsFetching(false));
  } else {
    yield put(setClaimsFetching(true));
    const result: ApiResponse<ClaimsServerResponse> = yield call(
      HealAPI.fetchClaims,
      state.ui.claimsFromDate,
      state.ui.claimsToDate,
      state.ui.claimsStatus,
      state.ui.claimsSearchText
    );
    yield put(setClaims((result.data && result.data.content) || []));
    yield put(setClaimsFetching(false));
  }
}

function* watchGenerateClaim() {
  yield takeEvery([ClaimsAction.SUBMIT_CLAIM], handleSubmitClaim);
  yield takeEvery([ClaimsAction.GENERATE_CLAIM], handleEvaluateVisitForClaimGeneration);
  yield takeEvery([ClaimsAction.REFRESH_CLAIM], handleRefreshClaim);
  yield takeEvery([ClaimsAction.RESET_CLAIM], handleResetClaim);
  yield takeEvery([ClaimsAction.FETCH_ONE_CLAIM_VISIT_CODE], handleFetchOneClaimByVisitCodeAction);
  yield takeEvery([ClaimsAction.FETCH_ONE_CLAIM], handleFetchOneClaimAction);
}

function* handleFetchOneClaimAction(message: ReduxAction<string>) {
  yield fetchOneClaim(message.data);
}

function* handleFetchOneClaimByVisitCodeAction(message: ReduxAction<string>) {
  yield fetchOneClaimByVisitCode(message.data);
}

function* fetchOneClaim(id: string): any {
  const response = yield call(HealAPI.fetchClaimByID, id);
  if (response.ok && response.data) {
    yield put(receivedUpdatedClaim(response.data));
  }
}

function* fetchOneClaimByVisitCode(visitCode: string): any {
  const response = yield call(HealAPI.fetchClaimsByVisitCode, visitCode);
  if (response.ok && response.data) {
    const latestClaim: Claim | undefined = response.data.sort((a: any, b: any) => {
      return b.revision - a.revision;
    })[0];
    if (latestClaim) {
      yield put(receivedUpdatedClaim(latestClaim));
    }
  }
}

function* handleSubmitClaim(message: ReduxAction<string>): any {
  const response = yield call(HealAPI.submitClaim, message.data);
  if (response.ok && response.data) {
    yield put(toast({ type: 'success', title: 'Submitted', content: 'Claim submitted successfully.' }));
    yield put(receivedUpdatedClaim(response.data));
  } else if (response.data) {
    yield fetchOneClaim(message.data);
    const error: any = response.data;
    yield put(toast({ type: 'error', title: 'Submission Error', content: error.message }));
  }
}

function* handleRefreshClaim(message: ReduxAction<string>): any {
  const response = yield call(HealAPI.refreshClaim, message.data);
  if (response.ok && response.data) {
    yield put(
      toast({
        type: 'info',
        title: 'Info',
        content: 'Contacting payer for updated claim information...',
      })
    );
    yield delay(5000);
    yield fetchOneClaim(response.data.id);
  } else if (response.data) {
    yield fetchOneClaim(message.data);
    const error: any = response.data;
    yield put(toast({ type: 'error', title: 'Error', content: error.message }));
  }
}

function* handleResetClaim(message: ReduxAction<string>): any {
  const response = yield call(HealAPI.resetClaim, message.data);
  if (response.ok && response.data) {
    yield put(toast({ type: 'info', title: 'Info', content: 'Claim state has been reset to "Submitted".' }));
    yield put(receivedUpdatedClaim(response.data));
  } else if (response.data) {
    yield fetchOneClaim(message.data);
    const error: any = response.data;
    yield put(toast({ type: 'error', title: 'Error', content: error.message }));
  }
}

function* handleEvaluateVisitForClaimGeneration(
  message: ReduxAction<{
    visitCode: string;
    ignoreOldDataCheck: boolean;
    forceFinalizedClaimReplacement: boolean;
  }>
): any {
  let visitSource = 'backend';

  if (message.data.visitCode.startsWith('HUB-')) {
    visitSource = 'hub_encounter';
  }

  const req: ClaimEvaluationRequest = {
    visitReference: message.data.visitCode,
    forceFinalizedClaimReplacement: message.data.forceFinalizedClaimReplacement,
    skipOldDataCheck: message.data.ignoreOldDataCheck,
    preventSubmission: true,
  };

  const response = yield call(HealAPI.evaluateForClaimGeneration, visitSource, req);
  if (response.ok && response.data) {
    let toastMessage = 'An unknown error has occurred.';
    switch (response.data.operation) {
      case 'NoAction':
        toastMessage = 'Claim has already been created with the latest information.  There is no action to take.';
        break;
      case 'Create':
        toastMessage = 'Claim has been created.';
        break;
      case 'Update':
        toastMessage = 'Claim has been updated with the latest information.';
        break;
      default:
        break;
    }
    if (response.data.operation !== 'NoAction') {
      if (response.data.shouldSubmitToPayers) {
        toastMessage += ' Claim will be submitted.';
      } else {
        toastMessage += ' Claim was not submitted and needs more information.';
      }
    }
    if (response.data.shouldSubmitToPayers) {
      yield put(toast({ type: 'success', title: 'Success', content: toastMessage }));
    } else {
      yield put(toast({ type: 'info', title: 'Info', content: toastMessage }));
    }
  } else {
    yield put(
      toast({
        type: 'error',
        title: 'Error',
        content: 'An error occurred submitting claim.  Check if the visit has ended and is insured.',
      })
    );
  }
  yield delay(1500);
  yield fetchOneClaimByVisitCode(message.data.visitCode);
}

export function* claimsFlow() {
  yield all([call(fetchDataFlow), call(watchGenerateClaim), call(fetchDataFlowSearchText)]);
}

function* fetchDataFlowSearchText() {
  yield takeLatest([UIAction.SET_CLAIMS_SEARCH_TEXT], debounceGetClaimsData);
}

function* debounceGetClaimsData() {
  yield delay(250);
  yield getClaimsData();
}

export function* fetchDataFlow() {
  yield takeLatest([UIAction.SET_CLAIMS_STATUS, UIAction.SET_CLAIMS_FROM_DATE, UIAction.SET_CLAIMS_TO_DATE], debounceGetClaimsData);
}
